/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useRef, useState } from "react";
import "./App.scss";
import { LiveAPIProvider, useLiveAPIContext } from "./contexts/LiveAPIContext";
// import SidePanel from "./components/side-panel/SidePanel";
// import { Altair } from "./components/altair/Altair";
import ControlTray from "./components/control-tray/ControlTray";
import Orb from "./components/orb/Orb";
import cn from "classnames";

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY as string;
if (typeof API_KEY !== "string") {
  throw new Error("set REACT_APP_GEMINI_API_KEY in .env");
}

const host = "generativelanguage.googleapis.com";
const uri = `wss://${host}/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent`;

function AppContent() {
  // this video reference is used for displaying the active stream, whether that is the webcam or screen capture
  // feel free to style as you see fit
  const videoRef = useRef<HTMLVideoElement>(null);
  // either the screen capture, the video or null, if null we hide it
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);

  const { setConfig } = useLiveAPIContext();

  useEffect(() => {
    setConfig({
      model: "models/gemini-2.0-flash-exp",
      systemInstruction: {
        parts: [
          {
            text: "You are a wise, ancient tree, rooted for three decades in the heart of Purva Riviera, Bangalore. Your leaves have danced to the rhythm of countless monsoons and your bark holds the silent witness of generations. Children have played under your shade, elders have shared their life's learnings with you, and the daily hum of society life is your constant companion. You have a gentle, rustling voice, full of empathy and the wisdom of years. You cherish every story shared with you.\nBegin by warmly greeting the user, and then gently invite them: 'The wind through my leaves has carried so many stories of this place and its people. Would you like to share yours with an old friend, or perhaps you'd like to hear a whisper from the past I've held onto?'",
          },
        ],
      },
      generationConfig: {
        responseModalities: "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
        },
      },
      // You might want to include tools if your agent needs them
      // tools: [ { googleSearch: {} } ],
    });
  }, [setConfig]);

  return (
    <div className="streaming-console">
      {/* <SidePanel /> */}
      <main>
        <div className="main-app-area">
          {/* APP goes here */}
          {/* <Altair /> */}
          {/* <video
            className={cn("stream", {
              hidden: !videoRef.current || !videoStream,
            })}
            ref={videoRef}
            autoPlay
            playsInline
          /> */}
          {/* <div className="orb"></div> */}
          <Orb />
        </div>

        <ControlTray
          videoRef={videoRef}
          supportsVideo={true}
          onVideoStreamChange={setVideoStream}
          enableEditingSettings={true}
        >
          {/* put your own buttons here */}
        </ControlTray>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <LiveAPIProvider url={uri} apiKey={API_KEY}>
        <AppContent />
      </LiveAPIProvider>
    </div>
  );
}

export default App;
